<script setup>
import { userAuthState } from "@/@core/auth"
import { MODULES } from '@/@core/utils/helpers'
import logout from '@/services/logout'
import { VNodeRenderer } from "@layouts/components/VNodeRenderer"
import { themeConfig } from "@themeConfig"
import { computed } from "vue"
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: "blank",
  },
})

const router = useRouter()
const auth = userAuthState()

const computedModules = computed(() => {
  let data = []
  auth?.user?.modules?.forEach(element => {
    data = [
      ...data,
      MODULES.find(x => x.name === element.name),
    ]
  })

  return data.filter(Boolean)
})

const menuItems = [
  {
    title: "Global Settings",
    icon: "tabler-settings",
    action: () => router.push('/app/global/users'),
  },
  {
    title: "Log Out",
    icon: "tabler-logout",
    action: logout,
  },
]

// Get greeting based on time
const greeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good Morning'
  if (hour < 18) return 'Good Afternoon'

  return 'Good Evening'
})
</script>

<template>
  <div class="panel-wrapper">
    <VContainer
      fluid
      class="panel-container"
    >
      <!-- Header -->
      <VCard
        flat
        class="header-card mb-6"
      >
        <VCardText class="d-flex align-center justify-space-between pa-4 pa-sm-6">
          <VNodeRenderer
            :nodes="themeConfig.app.client.logo"
            class="logo"
          />

          <!-- Desktop Menu -->
          <div class="d-sm-flex d-none gap-2">
            <VBtn
              v-for="item in menuItems"
              :key="item.title"
              variant="text"
              color="default"
              @click="item.action"
            >
              <VIcon
                size="20"
                :icon="item.icon"
                class="me-2"
              />
              {{ item.title }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <!-- Welcome Section -->
      <div class="welcome-section mb-6">
        <h2 class="text-h4 text-sm-h3 font-weight-bold mb-2">
          {{ greeting }}, {{ auth.user?.name || 'User' }}! 👋
        </h2>
        <p class="text-body-2 text-sm-body-1 mb-0">
          Welcome to {{ themeConfig.app.title }}. Select a module below to get started.
        </p>
      </div>

      <!-- Modules Grid -->
      <VRow v-if="computedModules.length > 0">
        <VCol
          v-for="(module, index) in computedModules"
          :key="module.name"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
        >
          <RouterLink
            :to="module.to"
            class="module-link"
          >
            <VCard
              class="module-card"
              hover
            >
              <VCardText class="d-flex flex-column align-center text-center pa-4 pa-sm-5">
                <div class="module-icon-wrapper mb-3 mb-sm-4">
                  <VIcon
                    :icon="module.icon"
                    :size="$vuetify.display.xs ? 40 : 48"
                    class="module-icon"
                  />
                </div>

                <h4 class="text-subtitle-1 text-sm-h6 font-weight-bold mb-2">
                  {{ module.name }}
                </h4>

                <p class="text-caption text-sm-body-2 text-medium-emphasis mb-0">
                  {{ module.desc }}
                </p>
              </VCardText>
            </VCard>
          </RouterLink>
        </VCol>
      </VRow>

      <!-- No Modules Message -->
      <VCard
        v-else
        class="text-center pa-12"
      >
        <VIcon
          icon="tabler-folder-off"
          size="80"
          color="warning"
          class="mb-4"
        />
        <h3 class="text-h4 mb-2">
          No Modules Available
        </h3>
        <p class="text-body-1 text-medium-emphasis">
          You don't have access to any modules yet. Please contact your administrator.
        </p>
      </VCard>

      <!-- Mobile Bottom Buttons -->
      <VRow class="d-sm-none mt-6">
        <VCol cols="12">
          <div class="mobile-buttons-wrapper">
            <VBtn
              v-for="item in menuItems"
              :key="item.title"
              variant="outlined"
              :color="item.title === 'Log Out' ? 'error' : 'primary'"
              size="small"
              class="mobile-action-btn"
              @click="item.action"
            >
              <VIcon
                size="18"
                :icon="item.icon"
              />
              <span class="ms-2">{{ item.title }}</span>
            </VBtn>
          </div>
        </VCol>
      </VRow>

      <!-- Footer -->
      <VRow class="mt-8 mt-sm-12">
        <VCol cols="12">
          <VDivider class="mb-6" />
        </VCol>
        <VCol cols="12">
          <div class="footer-section">
            <p class="text-caption text-sm-body-2 text-medium-emphasis mb-0">
              © {{ new Date().getFullYear() }} {{ themeConfig.app.title }}. All rights reserved.
            </p>
          </div>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style scoped>
.panel-wrapper {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.08) 0%, rgba(var(--v-theme-surface), 1) 100%);
  min-block-size: 100vh;
  padding-block: 1rem;
  padding-inline: 0.5rem;
}

.panel-container {
  margin-inline: auto;
  max-inline-size: 1400px;
}

.header-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 8%);
}

.logo {
  max-inline-size: 140px;
}

.welcome-section {
  padding-block: 1.5rem;
  padding-inline: 0.5rem;
}

.module-link {
  display: block;
  block-size: 100%;
  text-decoration: none;
}

.module-card {
  border: 2px solid transparent;
  border-radius: 12px;
  background: rgba(var(--v-theme-surface), 1);
  block-size: 100%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 8%);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.module-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.5);
  box-shadow: 0 12px 24px rgba(var(--v-theme-primary), 0.15);
  transform: translateY(-8px);
}

.module-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.15) 0%, rgba(var(--v-theme-primary), 0.08) 100%);
  block-size: 70px;
  inline-size: 70px;
  transition: all 0.3s ease;
}

.module-card:hover .module-icon-wrapper {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.25) 0%, rgba(var(--v-theme-primary), 0.15) 100%);
  transform: scale(1.1);
}

.module-icon {
  color: rgba(var(--v-theme-primary), 1);
}

.footer-section {
  padding-block: 0.5rem;
  text-align: center;
}

.mobile-buttons-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.mobile-action-btn {
  flex: 1;
  font-weight: 500;
  letter-spacing: normal;
  max-inline-size: 180px;
  min-inline-size: 140px;
  text-transform: none;
}

/* Mobile optimizations */
@media (max-width: 960px) {
  .panel-wrapper {
    padding-block: 0.5rem;
  }

  .welcome-section {
    padding-block: 1rem;
  }

  .logo {
    max-inline-size: 120px;
  }

  .module-icon-wrapper {
    block-size: 65px;
    inline-size: 65px;
  }

  .footer-section {
    padding-block: 0.5rem;
  }
}

@media (max-width: 600px) {
  .panel-wrapper {
    padding-inline: 0;
  }

  .welcome-section {
    padding-inline: 1rem;
  }

  .header-card {
    border-radius: 8px;
  }

  .module-card {
    border-radius: 10px;
  }

  .module-icon-wrapper {
    block-size: 60px;
    inline-size: 60px;
  }

  .mobile-buttons-wrapper {
    gap: 0.5rem;
    padding-inline: 1rem;
  }

  .mobile-action-btn {
    font-size: 0.875rem;
    min-inline-size: 120px;
  }
}

/* Ensure equal height cards across all breakpoints */
.v-col {
  display: flex;
}

/* Animation for module cards */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.module-card {
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.module-card:nth-child(1) { animation-delay: 0.05s; }
.module-card:nth-child(2) { animation-delay: 0.1s; }
.module-card:nth-child(3) { animation-delay: 0.15s; }
.module-card:nth-child(4) { animation-delay: 0.2s; }
.module-card:nth-child(5) { animation-delay: 0.25s; }
.module-card:nth-child(6) { animation-delay: 0.3s; }
.module-card:nth-child(7) { animation-delay: 0.35s; }
.module-card:nth-child(8) { animation-delay: 0.4s; }
</style>
